import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StringParam, useQueryParams } from "use-query-params";
import useSWR from "swr";
import { EntityType } from "../../types/types.MDM";
import {
  formatDate,
  makeUrlWithParams,
  TablePlaceholder,
  toTitleCase,
  useStoreState,
} from "../../util/util";
import { useDebounce } from "../../util/hooks";
import { createChangePerPageHandler } from "../Pagination/util";
import { TablePageLayout } from "../TablePage/TablePageLayout";
import { endpoints } from "../../endpoints";
import {
  ChangeRequestContextType,
  ChangeRequestStatus,
  ChangeRequestStatusType,
} from "./changerequest.util";
import { TableCellTwoLine } from "../Table/TableCellTwoLine";
import { Row } from "react-table";
import { IDDesktop } from "../IDs/IDs";
import { fontSizeTokens } from "../../designTokens";
import {
  ChangeRequestDetailPage,
  ChangeRequestUser,
} from "./ChangeRequestDetailPage";
import { FilterBy } from "../FilterBy/FilterBy";
import type { ChipType } from "../Chips/Chips";

export const CHANGE_REQUEST_TAB_KEY = "change_request";

interface ChangeRequestReponse {
  id: string;
  number: number;
  context_type: ChangeRequestContextType;
  context_label: string;
  details: string;
  status: ChangeRequestStatusType;
  requested_by: ChangeRequestUser;
  assignee?: ChangeRequestUser;
  created_at: string;
}

export type ChangeRequestsPaginatedOutput = {
  pagination: {
    total: number;
    offset: number;
    limit: number;
  };
  data: ChangeRequestReponse[];
};

type FilterOption = { id: string; name: string };

/**
 * Options for each filter dropdown. The BE applies every active filter to
 * every facet, so these always narrow down to values that match at least one
 * request (and go empty when nothing matches).
 */
export type ChangeRequestFilters = {
  statuses: string[];
  requested_by: FilterOption[];
  assignees: FilterOption[];
  context_types: string[];
  attribute_groups: FilterOption[];
  collections: FilterOption[];
  assets: FilterOption[];
};

/** Selected filter values, keyed by the list endpoint's query param name. */
type SelectedFilters = { [param: string]: string[] };

const toChips = (options: FilterOption[]): ChipType[] =>
  options.map(({ id, name }) => ({ id, name }));

const toLabelledChips = (values: string[]): ChipType[] =>
  values.map((value) => ({ id: value, name: toTitleCase(value) }));

type ChangeRequestsListPageProps = {
  entityId: string;
  isTabActive: boolean;
  entityType?: EntityType;
  canEdit: boolean;
};

export function ChangeRequestsListPage({
  entityType,
  entityId,
  isTabActive,
  canEdit,
}: ChangeRequestsListPageProps): JSX.Element {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  // The selected change request lives in the URL (?changeRequestId=...), which
  // makes the detail view deep-linkable and refresh/back-button safe.
  const [{ changeRequestId: selectedId }, setQuery] = useQueryParams({
    changeRequestId: StringParam,
    tabKey: StringParam,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(20);
  // Filters are component state rather than URL state: this table is a tab on
  // the profile/product detail page, so writing them to the page URL would
  // clobber the page's own params and add a history entry per filter change.
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      const id = e.currentTarget.id;
      if (id) setQuery({ changeRequestId: id, tabKey: CHANGE_REQUEST_TAB_KEY });
    },
    [setQuery]
  );

  const handleBack = useCallback(() => {
    setQuery({ changeRequestId: undefined });
  }, [setQuery]);

  const columns = useMemo(
    () => [
      {
        header: t("Request ID"),
        accessorKey: "number",
        cell: (cell: { getValue: () => number }) => (
          <IDDesktop style={{ fontSize: fontSizeTokens.font_size_sm }}>
            {cell.getValue()}
          </IDDesktop>
        ),
      },
      {
        header: t("Group / Collection / Asset"),
        id: "context",
        cell: ({ row }: { row: Row<ChangeRequestReponse> }) => (
          <TableCellTwoLine
            primaryText={row.original.context_label}
            secondaryText={toTitleCase(row.original.context_type)}
          />
        ),
      },
      {
        header: t("Request details"),
        accessorKey: "details",
      },
      {
        header: t("Requested By"),
        accessorKey: "requested_by",
      },
      {
        header: t("Assignee"),
        accessorKey: "assignee",
      },
      {
        header: t("Status"),
        accessorKey: "status",
        id: "status",
        enableSorting: false,
        align: "left",
        cell: (cell: { getValue: () => ChangeRequestStatusType }) => (
          <ChangeRequestStatus status={cell.getValue()} />
        ),
      },
    ],
    [t]
  );

  const endpoint = entityType
    ? endpoints.v2_tenants_id_entities_id_change_requests(
        tenant_id,
        (entityType + "s") as EntityType,
        entityId
      )
    : endpoints.v2_tenants_id_pim_products_id_change_requests(
        tenant_id,
        entityId
      );

  const filtersEndpoint = entityType
    ? endpoints.v2_tenants_id_entities_id_change_requests_filters(
        tenant_id,
        (entityType + "s") as EntityType,
        entityId
      )
    : endpoints.v2_tenants_id_pim_products_id_change_requests_filters(
        tenant_id,
        entityId
      );

  const listKey = useMemo(
    () =>
      makeUrlWithParams(endpoint, {
        offset,
        limit: perPage,
        q: debouncedSearchQuery || undefined,
        ...selectedFilters,
      }),
    [endpoint, offset, perPage, debouncedSearchQuery, selectedFilters]
  );

  // The search term and the active filters are sent along so the options stay
  // in step with what the table is actually showing.
  const filtersKey = useMemo(
    () =>
      makeUrlWithParams(filtersEndpoint, {
        q: debouncedSearchQuery || undefined,
        ...selectedFilters,
      }),
    [filtersEndpoint, debouncedSearchQuery, selectedFilters]
  );

  const { data, error, isValidating, mutate } =
    useSWR<ChangeRequestsPaginatedOutput>(listKey, {
      revalidateOnFocus: false,
    });

  const { data: filterOptions, mutate: mutateFilterOptions } =
    useSWR<ChangeRequestFilters>(filtersKey, { revalidateOnFocus: false });

  const listVisible = isTabActive && !selectedId;
  useEffect(() => {
    if (listVisible) {
      mutate();
      mutateFilterOptions();
    }
  }, [listVisible, mutate, mutateFilterOptions]);

  // Memoized as a unit: FilterBy resets its in-progress chip selection whenever
  // the identity of preselectedFilters changes, so these must stay stable
  // across unrelated re-renders (e.g. SWR revalidation).
  const filterControls = useMemo(() => {
    const build = (param: string, options: ChipType[]) => ({
      param,
      options,
      preselected: options.filter((option) =>
        (selectedFilters[param] ?? []).includes(String(option.id))
      ),
    });

    // Order matches the design: Requested By, Group, Collection, Asset Name,
    // Status. The BE also returns context_types and assignees facets; the
    // design does not surface them, so they are intentionally not rendered.
    return {
      requestedBy: build(
        "requested_by_ids",
        toChips(filterOptions?.requested_by ?? [])
      ),
      attributeGroups: build(
        "attribute_group_ids",
        toChips(filterOptions?.attribute_groups ?? [])
      ),
      collections: build(
        "collection_ids",
        toChips(filterOptions?.collections ?? [])
      ),
      assets: build("asset_ids", toChips(filterOptions?.assets ?? [])),
      statuses: build(
        "statuses",
        toLabelledChips(filterOptions?.statuses ?? [])
      ),
    };
  }, [filterOptions, selectedFilters]);

  const handleFilterChange = useCallback(
    ({ values, filter }: { values: ChipType[]; filter: string }) => {
      setSelectedFilters((previous) => {
        const next = { ...previous };
        const ids = values.map((value) => String(value.id));
        if (ids.length) {
          next[filter] = ids;
        } else {
          delete next[filter];
        }
        return next;
      });
      setOffset(0);
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setSelectedFilters({});
    setOffset(0);
  }, []);

  const hasActiveFilters = Object.values(selectedFilters).some(
    (values) => values.length > 0
  );

  const tableData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((item) => ({
      ...item,
      requested_by: `${item.requested_by.firstname} ${item.requested_by.lastname}`,
      assignee: item.assignee
        ? `${item.assignee?.firstname} ${item.assignee?.lastname}`
        : "-",
      created_at: formatDate(item.created_at),
    }));
  }, [data]);

  const pagination = useMemo(() => {
    if (!data?.pagination) {
      return { perPage, pageCount: 0, pageIndex: 1 };
    }
    const { total, limit } = data.pagination;
    return {
      perPage: limit,
      pageCount: Math.ceil(total / limit),
      pageIndex: Math.floor(offset / limit) + 1,
    };
  }, [data?.pagination, offset, perPage]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOffset(0);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const isLoading = isValidating && !data;

  return (
    <>
      {selectedId ? (
        <ChangeRequestDetailPage
          key={selectedId}
          changeRequestId={selectedId}
          onBack={handleBack}
          canEdit={canEdit}
        />
      ) : (
        <TablePageLayout
          showTitle={true}
          searchQuery={searchQuery}
          searchPlaceholder={t("Search")}
          onSearchChange={handleSearch}
          onSearchClear={handleClearSearch}
          filtersSlot={
            <>
              {filterControls.requestedBy.options.length > 0 && (
                <FilterBy
                  setter={handleFilterChange}
                  options={filterControls.requestedBy.options}
                  header={t("Requested By")}
                  filter={filterControls.requestedBy.param}
                  label={t("Requested By")}
                  preselectedFilters={filterControls.requestedBy.preselected}
                  size="small"
                />
              )}
              {filterControls.attributeGroups.options.length > 0 && (
                <FilterBy
                  setter={handleFilterChange}
                  options={filterControls.attributeGroups.options}
                  header={t("Group")}
                  filter={filterControls.attributeGroups.param}
                  label={t("Group")}
                  preselectedFilters={
                    filterControls.attributeGroups.preselected
                  }
                  size="small"
                />
              )}
              {filterControls.collections.options.length > 0 && (
                <FilterBy
                  setter={handleFilterChange}
                  options={filterControls.collections.options}
                  header={t("Collection")}
                  filter={filterControls.collections.param}
                  label={t("Collection")}
                  preselectedFilters={filterControls.collections.preselected}
                  size="small"
                />
              )}
              {filterControls.assets.options.length > 0 && (
                <FilterBy
                  setter={handleFilterChange}
                  options={filterControls.assets.options}
                  header={t("Asset Name")}
                  filter={filterControls.assets.param}
                  label={t("Asset Name")}
                  preselectedFilters={filterControls.assets.preselected}
                  size="small"
                />
              )}
              {filterControls.statuses.options.length > 0 && (
                <FilterBy
                  setter={handleFilterChange}
                  options={filterControls.statuses.options}
                  header={t("Status")}
                  filter={filterControls.statuses.param}
                  label={t("Status")}
                  preselectedFilters={filterControls.statuses.preselected}
                  size="small"
                />
              )}
            </>
          }
          showClearAll={hasActiveFilters}
          onClearAll={handleClearAllFilters}
          clearAllLabel={t("Clear All")}
          totalCount={data?.pagination?.total ?? 0}
          totalCountLabel={t(
            (data?.pagination?.total ?? 0) === 1
              ? "Change Request"
              : "Change Requests"
          )}
          perPageItems={perPageItems}
          perPage={perPage}
          onPerPageChange={changePerPage}
          columns={columns}
          data={tableData}
          isLoading={isLoading}
          rowClick={handleRowClick}
          error={error}
          Placeholder={<TablePlaceholder message={t("No results found")} />}
          pagination={pagination.pageCount > 1 ? pagination : undefined}
          offset={offset}
          onPageChange={setOffset}
        />
      )}
    </>
  );
}
